<template>
  <aside class="sidebar">
    <section class="profile">
      <div class="about">
        <h1 class="sr-only">{{ profile.firstName }} {{ profile.lastName }}</h1>

        <p class="first-name">
          {{ profile.firstName }}
        </p>

        <p class="last-name">
          {{ profile.lastName }}
        </p>

        <p class="profession">
          {{ pick(profile.i18n).profession }}
        </p>
      </div>
    </section>

    <div class="sep" />

    <address>
      <a
        v-for="item in contactLinks"
        :key="item.id"
        :href="item.url"
        :target="item.target"
        :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined"
        :class="item.class"
      >
        <i :class="item.icon" />

        <span>{{ pick(item.i18n).label }}</span>
      </a>
    </address>

    <nav aria-label="Main navigation">
      <router-link
        v-for="item in menuItems"
        :key="item.id"
        :to="navTarget(item)"
        @click="onNavClick(item, $event)"
      >
        <i :class="item.icon" />

        <span>{{ pick(item.i18n).label }}</span>
      </router-link>
    </nav>

    <div class="sidebar-controls">
      <ThemeToggle />
      <LocaleToggle />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { useLocalized } from '@/composables/useLocalized';
import { profile } from '@/data/profile';
import { contactLinks } from '@/data/contact';
import { menuItems } from '@/data/menu';
import type { MenuItem } from '@/data/types';
import ThemeToggle from './ThemeToggle.vue';
import LocaleToggle from './LocaleToggle.vue';

const route = useRoute();
const { pick } = useLocalized();

function navTarget(item: MenuItem) {
  return { path: item.path, hash: item.hash };
}

function scrollToHash(hash: string) {
  const el = document.querySelector(hash);
  if (!el) return;

  const prefersReduced = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

  el.scrollIntoView({
    behavior: prefersReduced ? 'auto' : 'smooth',
    block: 'start',
  });
}

function onNavClick(item: MenuItem, event: MouseEvent) {
  if (!item.hash || route.path !== '/') return;

  event.preventDefault();
  scrollToHash(item.hash);
  globalThis.history.replaceState(null, '', item.hash);
}
</script>

<style scoped lang="scss">
.sidebar {
  position: relative;
  z-index: 1;
  width: 320px;
  height: 100vh;
  padding: 15px;
  background: #1b1c1b;
  display: flex;
  flex-direction: column;

  @media (max-width: 425px) {
    width: 100%;
    height: auto;
  }

  .sidebar-controls {
    margin-top: auto;
    padding-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;

    @media (max-width: 425px) {
      margin-top: 20px;
    }
  }

  .profile {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 50px;

    .about {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      padding: 0 10px;
      max-width: 220px;

      .first-name,
      .last-name,
      .profession {
        display: table;
        padding: 5px;
        margin: 5px;
        font-size: 18px;
        text-align: center;
        font-weight: 600;
      }

      .first-name {
        color: #fff;
      }

      .last-name {
        background: #b4d455;
      }

      .profession {
        background: #ffae00;
        text-transform: uppercase;
        padding: 10px;
      }
    }
  }

  address {
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 425px) {
      margin-bottom: 40px;
    }

    a {
      margin: 0 10px;
      color: #fff;
      font-size: 20px;
      position: relative;
      transition: all 0.3s ease;

      &::before {
        content: '';
        position: absolute;
        z-index: 0;
        top: 50%;
        left: 50%;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        transition: all 0.3s ease;
        background: #ffae00;
        opacity: 0;
        width: 0;
        height: 0;
        pointer-events: none;
      }

      &:hover {
        color: #1b1c1b;

        &::before {
          width: 45px;
          height: 45px;
          opacity: 1;
        }
      }

      i {
        font-size: 30px;
        position: relative;
        z-index: 10;
      }

      span {
        width: 0;
        height: 0;
        overflow: hidden;
        display: block;
      }
    }
  }

  nav {
    margin-top: 50px;
    display: flex;
    flex-direction: column;

    @media (max-width: 425px) {
      display: none;
    }

    a {
      display: flex;
      padding: 15px 10px;
      font-size: 18px;
      text-align: center;
      font-weight: 600;
      color: #fff;
      transition: all 0.3s ease;

      i {
        min-width: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-right: 5px;
        transition: all 0.3s ease;
      }

      span {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 0;
          height: 3px;
          background: #b4d455;
          transition: all 0.3s ease;
        }
      }

      &:hover {
        background: #0d0e0d;

        i {
          color: #b4d455;
        }

        span::before {
          width: calc(100% - 20px);
        }
      }
    }
  }
}
</style>

<!-- Theme overrides: parent #app-root.light lives outside scoped boundary -->
<style lang="scss">
#app-root.light .sidebar {
  background: #c6c6c6;

  .profile .about .first-name {
    color: #1b1c1b;
  }

  address a,
  nav a {
    color: #000;
  }
}
</style>
